import { createModel } from '@rematch/core';
import { FilterOptionsParams } from 'components/RevxJobListFilter';
import { Paged } from 'domains/appDomain';
import {
  Billing,
  BillingInfo,
  BillingRequestor,
  BillingToInfo,
  Order,
  OrderHistoryItem,
  OrderHistorySearchGroupEnum,
  OrderItem,
  PaylodPlaceOrder,
  PaymentApiPayload,
  PaymentTypeEnum,
  PurchaseOrder,
  PurchaseOrderJob,
} from 'domains/orderDomain';
import { ShippingReadDatePayload } from 'domains/shippingDomain';
import { cloneDeep, sum } from 'lodash-es';
import { RootModel } from 'models';
import orderService from 'services/orderService';
import { BaseState, baseReducers } from './baseReducers';

interface OrderState extends BaseState {
  placeOrder: Partial<OrderItem>;
  orderHistory: OrderHistoryItem[];
  paymentToken: string;
  paymentType: PaymentTypeEnum;
  purchaseOrder: Partial<PurchaseOrder>;
  billingInfo?: Partial<BillingInfo>;
  billingRequestor?: Partial<BillingRequestor>;
  billToInfo?: Partial<BillingToInfo>;
  payOrderLoadId: string;
  resetOrderHistoryList?: () => void;
  orderPaymentData: string;
}

export const order = createModel<RootModel>()({
  state: {
    placeOrder: {},
    orderHistory: [],
    paymentToken: '',
    paymentType: PaymentTypeEnum.STRIPE_CC,
    purchaseOrder: {},
    billingInfo: {},
    billingRequestor: {},
    payOrderLoadId: '',
    resetOrderHistoryList: undefined,
    orderPaymentData: '',
    billToInfo: {},
  } as OrderState,
  reducers: {
    ...baseReducers<OrderState>(),
  },
  effects: (dispatch) => {
    const { order } = dispatch;
    return {
      async getPlaceOrder(payload: PaylodPlaceOrder, state): Promise<void> {
        const placeOrder = await orderService.placeOrder({
          ...payload,
          customerId: state.app.user?.userId as number,
        });
        order.setState({
          placeOrder,
        });
      },

      async orderCheckout(payload: PaymentApiPayload): Promise<string> {
        const paymentToken = await orderService.orderCheckout(payload);
        order.setState({
          paymentToken,
        });
        return paymentToken;
      },

      // async getHistoryList(payload, state) {
      //   if (state.app.user?.userId && state.app.user?.orgId) {
      //     const history = await orderService.getHistory(
      //       state.app.user?.userId,
      //       Number(localStorage.getItem('org')) || state.app.user?.orgId
      //     );
      //     order.setState({
      //       orderHistory: history.content,
      //     });
      //   } else {
      //     order.setState({
      //       orderHistory: [],
      //     });
      //   }
      // },

      async getHistoryList(
        {
          page,
          searchGroup,
          jobOrOrderNumber,
          jobName,
          supplierName,
          requesterName,
          startOrderDate,
          endOrderDate,
          orderId,
        }: {
          page: number;
          searchGroup: OrderHistorySearchGroupEnum;
          orderId?: string;
        } & FilterOptionsParams,
        state
      ): Promise<Paged<OrderHistoryItem>> {
        await dispatch.app.showLoading();
        try {
          let historyObj;
          historyObj = await orderService.getHistory({
            page: page,
            searchGroup,
            size: state.workspace.size,
            userId: state.app.user?.userId || 0,
            orgId:
              Number(localStorage.getItem('org')) || state.app.user?.orgId || 0,
            jobOrOrderNumber,
            jobName,
            supplierName,
            requesterName,
            startOrderDate,
            endOrderDate,
            orderId,
          });
          let historyList: OrderHistoryItem[] = historyObj.content;
          order.setState({
            orderHistory: historyList,
          });
          return historyObj;
        } catch {
          return {
            content: [],
            pageable: {
              sort: {
                sorted: false,
                unsorted: false,
                empty: false,
              },
              offset: 0,
              pageNumber: 0,
              pageSize: 0,
              paged: false,
              unpaged: false,
            },
            totalElements: 0,
            numberOfElements: 0,
            totalPages: 0,
            size: 0,
            number: 0,
            first: false,
            empty: false,
          };
        } finally {
          await dispatch.app.hideLoading();
        }
      },

      async cancelOrder(payload: number | string, state) {
        await orderService.orderCancel(payload);
        if (typeof state.order.resetOrderHistoryList === 'function') {
          state.order.resetOrderHistoryList();
        }
      },
      async resetOrder(payload, state) {
        if (typeof state.order.resetOrderHistoryList === 'function') {
          state.order.resetOrderHistoryList();
        }
      },
      async orderConfirm(payload: Partial<PurchaseOrder>): Promise<Order> {
        dispatch.app.showLoading();
        try {
          const res = await orderService.orderConfirm(payload);
          await dispatch.order.getPurchaseJob({
            ...res,
            id: res.id,
            jobIds: res.jobIds,
            status: res.status,
          });
          dispatch.cart.getList();
          dispatch.app.hideLoading();
          return res;
        } catch {
          dispatch.app.hideLoading();
          return {} as Order;
        }
      },

      async orderUpdate(payload: Partial<OrderItem>): Promise<OrderItem> {
        const placeOrder = await orderService.orderUpdate(payload);
        order.setState({
          placeOrder,
        });
        return placeOrder;
      },

      async getPurchaseJobDetail(
        payload: Partial<Order>,
        state
      ): Promise<PurchaseOrder> {
        order.setState({
          payOrderLoadId: payload.id,
        });
        const payloadJob = await orderService.getPurchaseJobList(
          payload?.jobIds || []
        );
        const jobs: PurchaseOrderJob[] = cloneDeep(
          (state.order.purchaseOrder?.jobs || []).map(
            (item: PurchaseOrderJob) => {
              const payloadJobItem = payloadJob.find(
                (job) => job.jobId === item.jobId
              );
              return payloadJobItem || item;
            }
          )
        );
        jobs.forEach((job) => {
          const weightTotal = sum(job.shippingItems.map((item) => item.weight));
          job.quantity = sum(job.shippingItems.map((item) => item.quantity));
          job.weightUnit = weightTotal / job.quantity;
          job.freightCharge = sum(
            job.shippingItems.map(
              (item) => item.costLineItems.revxFreightCharge
            )
          );
          job.printingCharge = sum(
            job.shippingItems.map(
              (item) => item.costLineItems.revxPrintingCharge
            )
          );
          job.totalCharge = sum(
            job.shippingItems.map((item) => item.costLineItems.revxTotalCharge)
          );
        });
        const defaultCharge: number = 0;
        const purchaseOrder: PurchaseOrder = {
          billingInfo: payload.billingInfo,
          billingRequestor: payload.billingRequestor,
          id: payload.id,
          userId: state.app.user?.userId || 0,
          orgId: state.app.user?.orgId || 0,
          remark: '',
          status: payload.status,
          printingCharge: payload.printingCharge || defaultCharge,
          freightCharge: payload.freightCharge || defaultCharge,
          taxCharge: payload.taxCharge || defaultCharge,
          totalCharge: payload.totalCharge || defaultCharge,
          jobs: jobs,
          requestor: payload.requestor,
          billingTo: payload.billingTo,
        };
        order.setState({
          purchaseOrder,
          paymentToken: '',
        });
        return purchaseOrder;
      },

      async getPurchaseJob(
        payload: Partial<Order>,
        state
      ): Promise<PurchaseOrder> {
        order.setState({
          payOrderLoadId: payload.id,
        });
        const jobs = await orderService.getPurchaseJobList(
          payload?.jobIds || []
        );
        jobs.forEach((job) => {
          const weightTotal = sum(job.shippingItems.map((item) => item.weight));
          job.quantity = sum(job.shippingItems.map((item) => item.quantity));
          job.weightUnit = weightTotal / job.quantity;
          job.freightCharge = sum(
            job.shippingItems.map(
              (item) => item.costLineItems.revxFreightCharge
            )
          );
          job.printingCharge = sum(
            job.shippingItems.map(
              (item) => item.costLineItems.revxPrintingCharge
            )
          );
          job.totalCharge = sum(
            job.shippingItems.map((item) => item.costLineItems.revxTotalCharge)
          );
        });
        const defaultCharge: number = 0;
        const purchaseOrder: PurchaseOrder = {
          billingInfo: payload.billingInfo,
          billingRequestor: payload.billingRequestor,
          id: payload.id,
          userId: state.app.user?.userId || 0,
          orgId: state.app.user?.orgId || 0,
          remark: '',
          status: payload.status,
          printingCharge: payload.printingCharge || defaultCharge,
          freightCharge: payload.freightCharge || defaultCharge,
          taxCharge: payload.taxCharge || defaultCharge,
          totalCharge: payload.totalCharge || defaultCharge,
          jobs,
          requestor: payload.requestor,
          billingTo: payload.billingTo,
        };
        order.setState({
          purchaseOrder,
          paymentToken: '',
        });
        return purchaseOrder;
      },

      async batchShippingReadyDate(
        payload: ShippingReadDatePayload,
        state
      ): Promise<any> {
        try {
          const res = await orderService.batchShippingReadyDate(payload);
          order.setState({
            purchaseOrder: {
              ...state.order.purchaseOrder,
              jobs: cloneDeep(
                (state.order.purchaseOrder?.jobs || []).map(
                  (item: PurchaseOrderJob) => {
                    const payloadJobItem = res.find(
                      (job) => job.jobId === item.jobId
                    );
                    return payloadJobItem || item;
                  }
                )
              ),
            },
          });
          return res || 'success';
        } catch {
          return 'error';
        }
      },

      async confirmPurchase(
        payload: Partial<Order>,
        state
      ): Promise<PurchaseOrder> {
        order.setState({
          payOrderLoadId: payload.id,
        });
        const purchaseWrapper = await orderService.confirmPurchase(
          payload?.jobIds || []
        );
        const jobs = purchaseWrapper.jobs;
        jobs?.forEach((job) => {
          const weightTotal = sum(job.shippingItems.map((item) => item.weight));
          job.quantity = sum(job.shippingItems.map((item) => item.quantity));
          job.weightUnit = weightTotal / job.quantity;
          job.freightCharge = sum(
            job.shippingItems.map(
              (item) => item.costLineItems.revxFreightCharge
            )
          );
          job.printingCharge = sum(
            job.shippingItems.map(
              (item) => item.costLineItems.revxPrintingCharge
            )
          );
          job.totalCharge = sum(
            job.shippingItems.map((item) => item.costLineItems.revxTotalCharge)
          );
        });
        const defaultCharge: number = 0;
        const purchaseOrder: PurchaseOrder = {
          billingInfo: payload.billingInfo,
          billingRequestor: payload.billingRequestor,
          id: payload.id,
          userId: state.app.user?.userId || 0,
          orgId: state.app.user?.orgId || 0,
          remark: '',
          status: payload.status,
          printingCharge: payload.printingCharge || defaultCharge,
          freightCharge: payload.freightCharge || defaultCharge,
          taxCharge: payload.taxCharge || defaultCharge,
          totalCharge: payload.totalCharge || defaultCharge,
          jobs,
          requestor: payload.requestor,
          billingTo: payload.billingTo,
        };
        console.log('after confirm purchase');
        order.setState({
          purchaseOrder,
          billingRequestor: purchaseWrapper.billingRequestor,
          billToInfo: purchaseWrapper.billingToInfo,
          billingInfo: purchaseWrapper.billingInfo,
          paymentToken: '',
        });
        return purchaseOrder;
      },

      async getOrderDetail(payload: Partial<Order>): Promise<Partial<Order>> {
        return await orderService.getOrderDetail(payload);
      },

      async orderSubmit(payload: Partial<Billing>, state): Promise<any> {
        await orderService.orderSubmit(payload);
        const orderRes = await order.getOrderDetail({ id: payload.orderId });
        order.setState({
          purchaseOrder: { ...state.order.purchaseOrder, ...orderRes },
        });
      },

      async updateOrderPaymentMethod(payload: {
        orderId: number | string;
        paymentMethodId: number | string;
      }): Promise<void> {
        await orderService.updateOrderPaymentMethod(
          payload.orderId,
          payload.paymentMethodId
        );
      },
    };
  },
});
